// Family settings + subscription management.
import { useEffect, useState } from "react";
import type { Home } from "@inkling/shared";
import { api } from "../lib/api.js";
import { useFamily } from "../lib/family.js";
import { useAuth } from "../lib/auth.js";
import { HeaderNav } from "../components/HeaderNav.js";

interface HomeFormState {
  label: string;
  location: string;
  timezone: string;
  isDefault: boolean;
}

function emptyHomeForm(timezone: string): HomeFormState {
  return {
    label: "",
    location: "",
    timezone,
    isDefault: false,
  };
}

export function SettingsPage() {
  const { family } = useFamily();
  const { logout } = useAuth();
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [usage, setUsage] = useState<{
    tier: "free" | "paid";
    eventsThisMonth: number;
    limit: number | null;
    overage: number;
    currentPeriodEnd: string | null;
    status: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleStatus, setGoogleStatus] = useState<
    | { connected: true; providerAccountId: string | null; expiresAt: string | null }
    | { connected: false }
    | null
  >(null);
  const [homes, setHomes] = useState<Home[]>([]);
  const [homesLoading, setHomesLoading] = useState(false);
  const [homeForm, setHomeForm] = useState<HomeFormState>(() => emptyHomeForm(browserTimezone));
  const [showHomeForm, setShowHomeForm] = useState(false);
  const [editingHomeId, setEditingHomeId] = useState<string | null>(null);
  const [homesBusy, setHomesBusy] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState<string | null>(null);
  const [homeStatus, setHomeStatus] = useState<string | null>(null);
  const [homeError, setHomeError] = useState<string | null>(null);

  const upgradeParam = new URLSearchParams(window.location.search).get("upgrade");
  const googleParam = new URLSearchParams(window.location.search).get("google");

  useEffect(() => {
    if (!family) return;
    if (!showHomeForm) {
      setHomeForm(emptyHomeForm(family.homeTimezone));
    }
  }, [family?.id, family?.homeTimezone, showHomeForm]);

  useEffect(() => {
    if (!family) return;
    void loadHomes(family.id);
  }, [family?.id]);

  useEffect(() => {
    if (!family) return;
    void api
      .googleStatus("family")
      .then((s) =>
        setGoogleStatus(
          s.connected
            ? {
                connected: true,
                providerAccountId: s.providerAccountId,
                expiresAt: s.expiresAt,
              }
            : { connected: false },
        ),
      )
      .catch(() => setGoogleStatus({ connected: false }));
  }, [family?.id]);

  useEffect(() => {
    if (!family) return;
    void api.usage(family.id).then(setUsage).catch(console.error);

    // After a successful Stripe Checkout, the webhook flips tier=paid in our
    // DB once Stripe pings us. That ping is asynchronous — typically arrives
    // within 1-3s of returning to /settings?upgrade=success. Poll a few times
    // so the user sees the new tier without a manual refresh.
    if (upgradeParam !== "success") return;
    const start = Date.now();
    const id = setInterval(async () => {
      try {
        const next = await api.usage(family.id);
        setUsage(next);
        if (next.tier === "paid" || Date.now() - start > 15000) {
          clearInterval(id);
        }
      } catch (err) {
        console.error("[settings] usage poll failed", err);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [family?.id, upgradeParam]);

  async function loadHomes(familyId: string) {
    setHomesLoading(true);
    try {
      const { homes } = await api.listHomes(familyId);
      setHomes(homes);
    } catch (err) {
      setHomeError(err instanceof Error ? err.message : "Couldn't load homes");
    } finally {
      setHomesLoading(false);
    }
  }

  function openAddHomeForm() {
    if (!family) return;
    setEditingHomeId(null);
    setShowHomeForm(true);
    setHomeError(null);
    setHomeStatus(null);
    setHomeForm(emptyHomeForm(family.homeTimezone));
  }

  function openEditHomeForm(home: Home) {
    setEditingHomeId(home.id);
    setShowHomeForm(true);
    setHomeError(null);
    setHomeStatus(null);
    setHomeForm({
      label: home.label,
      location: home.location ?? "",
      timezone: home.timezone,
      isDefault: home.isDefault,
    });
  }

  function closeHomeForm() {
    setEditingHomeId(null);
    setShowHomeForm(false);
    setHomeError(null);
    if (family) {
      setHomeForm(emptyHomeForm(family.homeTimezone));
    }
  }

  async function onSubmitHome(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!family) return;

    setHomesBusy(true);
    setHomeError(null);
    setHomeStatus(null);

    const body = {
      label: homeForm.label.trim(),
      location: homeForm.location.trim() || null,
      timezone: homeForm.timezone.trim(),
      isDefault: homeForm.isDefault,
    };

    try {
      if (editingHomeId) {
        await api.updateHome(family.id, editingHomeId, body);
        setHomeStatus("Home updated.");
      } else {
        await api.createHome(family.id, body);
        setHomeStatus("Home added.");
      }
      await loadHomes(family.id);
      closeHomeForm();
    } catch (err) {
      setHomeError(err instanceof Error ? err.message : "Couldn't save home");
    } finally {
      setHomesBusy(false);
    }
  }

  async function onDeleteHome(home: Home) {
    if (!family) return;
    const confirmed = window.confirm(
      `Delete ${home.label}? Events already tied to it will keep their timestamps, but this home will be removed.`,
    );
    if (!confirmed) return;

    setDeleteBusyId(home.id);
    setHomeError(null);
    setHomeStatus(null);
    try {
      await api.deleteHome(family.id, home.id);
      setHomeStatus("Home deleted.");
      await loadHomes(family.id);
    } catch (err) {
      setHomeError(err instanceof Error ? err.message : "Couldn't delete home");
    } finally {
      setDeleteBusyId(null);
    }
  }

  async function onUpgrade() {
    if (!family) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.subscribe(family.id);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't start checkout");
      setBusy(false);
    }
  }

  async function onPortal() {
    if (!family) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.portal(family.id);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't open the billing portal");
      setBusy(false);
    }
  }

  if (!family) return <div className="p-8 text-stone-600">Loading…</div>;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold">{family.name} family — settings</h1>
          <HeaderNav active="settings" />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-6 space-y-6">
        {upgradeParam === "success" && (
          <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
            {usage?.tier === "paid"
              ? "✓ Upgrade complete. Welcome to unlimited events."
              : "Confirming your upgrade with Stripe…"}
          </div>
        )}
        {upgradeParam === "cancelled" && (
          <div className="rounded-lg bg-stone-100 px-4 py-3 text-sm text-stone-700">
            No worries — your free plan is still active.
          </div>
        )}

        <section className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h2 className="font-medium mb-1">Family details</h2>
          <dl className="text-sm space-y-1 mt-4">
            <div className="flex"><dt className="w-40 text-stone-500">Name</dt><dd>{family.name}</dd></div>
            <div className="flex"><dt className="w-40 text-stone-500">Family number</dt><dd className="font-mono">{family.twilioPhoneNumber}</dd></div>
            <div className="flex"><dt className="w-40 text-stone-500">Home location</dt><dd>{family.homeLocation ?? "—"}</dd></div>
            <div className="flex"><dt className="w-40 text-stone-500">Timezone</dt><dd>{family.homeTimezone}</dd></div>
          </dl>
        </section>

        <section className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h2 className="font-medium">Homes</h2>
          <p className="mt-1 text-sm text-stone-600">
            Add multiple homes if your family splits time between locations — each home has its own timezone for event scheduling.
          </p>
          {homeStatus && <p className="mt-3 text-sm text-green-700">{homeStatus}</p>}
          {homeError && <p className="mt-3 text-sm text-red-600">{homeError}</p>}
          {homesLoading ? (
            <p className="mt-4 text-sm text-stone-500">Loading homes…</p>
          ) : (
            <>
              <ul className="mt-4 divide-y divide-stone-200">
                {homes.map((home) => {
                  const deleteDisabled = homes.length === 1 || home.isDefault;
                  const rowBusy = deleteBusyId === home.id;
                  return (
                    <li key={home.id} className="py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{home.label}</p>
                          {home.isDefault && (
                            <span className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-700">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-stone-600">
                          {home.location ? (
                            home.location
                          ) : (
                            <span className="italic">No location set</span>
                          )}
                          <span className="mx-2 text-stone-300">•</span>
                          {home.timezone}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => openEditHomeForm(home)}
                          disabled={homesBusy || deleteBusyId !== null}
                          className="text-xs px-2 py-1 rounded-md text-stone-700 hover:bg-stone-100 disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => void onDeleteHome(home)}
                          disabled={deleteDisabled || homesBusy || deleteBusyId !== null}
                          className="text-xs px-2 py-1 rounded-md text-red-700 hover:bg-red-100 disabled:opacity-50"
                        >
                          {rowBusy ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {showHomeForm ? (
                <form onSubmit={onSubmitHome} className="mt-4 rounded-xl border border-stone-200 p-4 space-y-3">
                  <div>
                    <label htmlFor="home-label" className="block text-sm font-medium text-stone-700 mb-1">
                      Label
                    </label>
                    <input
                      id="home-label"
                      type="text"
                      value={homeForm.label}
                      onChange={(e) => setHomeForm((prev) => ({ ...prev, label: e.target.value }))}
                      placeholder="Home, Cabin, Grandma's house"
                      className="input"
                      maxLength={80}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="home-location" className="block text-sm font-medium text-stone-700 mb-1">
                      Location
                    </label>
                    <input
                      id="home-location"
                      type="text"
                      value={homeForm.location}
                      onChange={(e) => setHomeForm((prev) => ({ ...prev, location: e.target.value }))}
                      placeholder="San Diego, CA"
                      className="input"
                      maxLength={160}
                    />
                  </div>
                  <div>
                    <label htmlFor="home-timezone" className="block text-sm font-medium text-stone-700 mb-1">
                      Timezone
                    </label>
                    <input
                      id="home-timezone"
                      type="text"
                      value={homeForm.timezone}
                      onChange={(e) => setHomeForm((prev) => ({ ...prev, timezone: e.target.value }))}
                      placeholder="America/Los_Angeles"
                      className="input"
                      required
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input
                      type="checkbox"
                      checked={homeForm.isDefault}
                      onChange={(e) => setHomeForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
                      className="h-4 w-4 rounded border-stone-300 text-stone-900 focus:ring-stone-400"
                    />
                    Make this the default home
                  </label>
                  <div className="flex items-center gap-2">
                    <button type="submit" disabled={homesBusy} className="btn-primary">
                      {homesBusy ? "Saving…" : editingHomeId ? "Save changes" : "Add home"}
                    </button>
                    <button type="button" onClick={closeHomeForm} disabled={homesBusy} className="btn-secondary">
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mt-4">
                  <button type="button" onClick={openAddHomeForm} className="btn-secondary">
                    Add home
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <section className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h2 className="font-medium">Subscription</h2>
          {usage ? (
            <>
              <div className="mt-4 flex items-baseline justify-between">
                <span className="text-3xl font-semibold capitalize">{usage.tier}</span>
                <span className="text-sm text-stone-600">
                  {usage.eventsThisMonth} event{usage.eventsThisMonth === 1 ? "" : "s"} this month
                  {usage.limit !== null && <> / {usage.limit}</>}
                </span>
              </div>
              {usage.tier === "free" && usage.limit !== null && (
                <div className="mt-4 h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-stone-900"
                    style={{ width: `${Math.min(100, (usage.eventsThisMonth / usage.limit) * 100)}%` }}
                  />
                </div>
              )}
              <p className="mt-3 text-sm text-stone-600">
                {usage.tier === "free"
                  ? "Free plan: 3 family members + 50 events/month. Upgrade for unlimited everything."
                  : `You're on the family plan${
                      usage.currentPeriodEnd
                        ? ` — renews ${new Date(usage.currentPeriodEnd).toLocaleDateString()}`
                        : ""
                    }. Update billing, view invoices, or cancel any time.`}
              </p>
              <div className="mt-4">
                {usage.tier === "free" ? (
                  <button onClick={onUpgrade} disabled={busy} className="btn-primary">
                    {busy ? "Loading…" : "Upgrade — $29.99/month"}
                  </button>
                ) : (
                  <button onClick={onPortal} disabled={busy} className="btn-secondary">
                    {busy ? "Loading…" : "Manage billing & plan"}
                  </button>
                )}
              </div>
              {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
            </>
          ) : (
            <p className="mt-4 text-stone-500 text-sm">Loading usage…</p>
          )}
        </section>

        <section className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h2 className="font-medium">Google Calendar (Phase 2 preview)</h2>
          {googleParam === "connected" && (
            <div className="mt-3 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              ✓ Connected to Google. Calendar sync wires up next.
            </div>
          )}
          {googleParam === "denied" && (
            <div className="mt-3 rounded-lg bg-stone-100 px-3 py-2 text-sm text-stone-700">
              No worries — you can connect Google Calendar later.
            </div>
          )}
          {googleParam === "upgrade_required" && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              Google Calendar sync is a paid feature — upgrade to connect it.{" "}
              <button onClick={onUpgrade} className="underline font-medium">
                Upgrade
              </button>
            </div>
          )}
          {googleStatus === null ? (
            <p className="mt-3 text-sm text-stone-500">Loading…</p>
          ) : googleStatus.connected ? (
            <>
              <p className="mt-3 text-sm text-stone-700">
                Family-level Google Calendar is connected.
              </p>
              <p className="mt-1 text-xs text-stone-500 break-all">
                {googleStatus.providerAccountId
                  ? `Linked Google account id: ${googleStatus.providerAccountId}`
                  : "Linked."}
              </p>
              <a
                href={api.googleStartUrl("family")}
                className="mt-4 inline-block btn-secondary"
              >
                Reconnect or switch account
              </a>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm text-stone-600">
                Connect a Google account so the family's events can sync into Google Calendar
                (read/write coming next iteration).
              </p>
              {usage?.tier === "free" ? (
                <button onClick={onUpgrade} disabled={busy} className="mt-4 inline-block btn-primary">
                  Upgrade to connect Google Calendar
                </button>
              ) : (
                <a
                  href={api.googleStartUrl("family")}
                  className="mt-4 inline-block btn-primary"
                >
                  Connect Google Calendar
                </a>
              )}
            </>
          )}
        </section>

        <section className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <button onClick={() => void logout().then(() => (window.location.href = "/"))} className="btn-secondary">
            Sign out
          </button>
        </section>
      </main>
    </div>
  );
}
